import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "tailwind-variants";

import { useDesignProjectSync } from "@design-lab/features/design-project/model/useDesignProjectSync";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { downloadCookieArtwork } from "@design-lab/shared/lib/cookieArtworkLoader";
import {
  DesignUploadCookie,
  type DesignUploadCookieData
} from "@design-lab/shared/lib/designUploadCookie";
import { Button, Modal, ModalBody, ModalHeader } from "@design-lab/shared/ui";
import { DESIGN_LAB_ACCEPT_CONFIG } from "common/artwork-file-types";
import { useDropzone } from "react-dropzone";
import { ContinueSection } from "./ContinueSection";

type StartDesigningModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onChooseDevice: () => void;
  onChooseLibrary: () => void;
  onChooseCanva: () => void;
  isLoggedIn: boolean;
  onDropFiles?: (files: File[]) => void;
};

export function StartDesigningModal(props: StartDesigningModalProps) {
  const {
    isOpen,
    onClose,
    onChooseDevice,
    onChooseLibrary,
    onChooseCanva,
    isLoggedIn,
    onDropFiles
  } = props;

  const { isMobile } = useResponsive();
  const [cookieArtwork, setCookieArtwork] = useState<DesignUploadCookieData | null>(null);
  const [isLoadingCookieArtwork, setIsLoadingCookieArtwork] = useState(false);

  // Get latest project and modal state from simplified hook
  const { latestProject, continueWithLatest, isLoading } = useDesignProjectSync();

  // Read cookie on mount
  useEffect(() => {
    const cookieData = DesignUploadCookie.read();
    setCookieArtwork(cookieData);
  }, []);

  // Extract project images from latest project
  const extractedProjectImages = useMemo(
    () => ({
      front:
        latestProject?.placements?.front?.dtfArtwork?.originalImageUrl ||
        latestProject?.placements?.frontLeftChest?.dtfArtwork?.originalImageUrl ||
        "",
      back: latestProject?.placements?.back?.dtfArtwork?.originalImageUrl || ""
    }),
    [latestProject]
  );

  // Handle continue with cookie artwork
  const handleContinueWithCookie = useCallback(async () => {
    if (!cookieArtwork || isLoadingCookieArtwork) return;

    setIsLoadingCookieArtwork(true);

    try {
      const file = await downloadCookieArtwork(cookieArtwork);

      // Delete the cookie after successful download
      DesignUploadCookie.delete();
      setCookieArtwork(null);
      setIsLoadingCookieArtwork(false);

      // Pass the file to the upload flow
      if (onDropFiles) {
        onDropFiles([file]);
      }

      onClose();
    } catch {
      // Delete the cookie even on error to prevent repeated failures
      DesignUploadCookie.delete();
      setCookieArtwork(null);
      setIsLoadingCookieArtwork(false);

      onChooseDevice();
      onClose();
    }
  }, [cookieArtwork, isLoadingCookieArtwork, onDropFiles, onChooseDevice, onClose]);

  const showContinueSection =
    cookieArtwork ||
    (isLoading && !latestProject) ||
    (latestProject && (extractedProjectImages.front || extractedProjectImages.back));
  const modalSizeClasses = cn(
    "flex flex-col h-5/6 w-11/12 lg:h-2/3",
    showContinueSection ? "lg:max-w-5xl" : "lg:max-w-3xl"
  )!;

  const { getRootProps, getInputProps } = useDropzone({
    accept: DESIGN_LAB_ACCEPT_CONFIG,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: files => onDropFiles?.(files)
  });
  const rootProps = getRootProps();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={true}
      closeOnEsc={true}
      className={modalSizeClasses}
      title="Start Designing"
      description="Choose to start a new design or continue where you left off"
    >
      <ModalHeader onClose={onClose}>Start Designing</ModalHeader>
      <ModalBody className="mb-6 flex flex-1 p-4 lg:mb-auto lg:p-6">
        <div
          className={cn("w-full", showContinueSection && "grid grid-cols-1 gap-4 lg:grid-cols-2")}
        >
          {showContinueSection && (
            <div className="flex flex-col items-center gap-4 rounded-xl bg-blue-50 p-4 lg:p-6">
              <h3 className="text-center text-xl font-bold">Continue where you left off</h3>
              <ContinueSection
                cookieArtwork={cookieArtwork}
                latestProject={latestProject}
                extractedProjectImages={extractedProjectImages}
                isLoading={isLoading}
                isLoadingCookieArtwork={isLoadingCookieArtwork}
                onContinueWithCookie={handleContinueWithCookie}
                onContinueWithLatest={() => {
                  continueWithLatest();
                  onClose();
                }}
              />
            </div>
          )}

          <div
            className={cn(
              "rounded-xl border-2 border-dashed border-blue-700",
              showContinueSection ? "p-4 lg:p-6" : "p-6"
            )}
            onDragEnter={rootProps.onDragEnter}
            onDragOver={rootProps.onDragOver}
            onDrop={rootProps.onDrop}
          >
            <div className="flex flex-col items-center gap-3">
              {!isMobile && (
                <>
                  <h3 className="text-xl font-bold">Drag your design here</h3>
                  <div className="flex items-center gap-4 text-gray-500">
                    <span className="inline-block h-px w-16 bg-gray-300" />
                    <span>or</span>
                    <span className="inline-block h-px w-16 bg-gray-300" />
                  </div>
                </>
              )}
              <div className="text-xl">Choose designs from</div>
              <div className="flex w-full max-w-sm flex-col items-center gap-3">
                <input {...getInputProps()} />
                <Button
                  data-testid="start-designing-device"
                  onPress={onChooseDevice}
                  className="h-12 w-44"
                >
                  My Device
                </Button>
                {isLoggedIn && (
                  <Button
                    data-testid="start-designing-library"
                    variant="outline"
                    onPress={onChooseLibrary}
                    className="h-12 w-44"
                  >
                    My Design Library
                  </Button>
                )}
                {isLoggedIn && (
                  <Button
                    data-testid="start-designing-canva"
                    variant="outline"
                    onPress={onChooseCanva}
                    className="h-12 w-44"
                  >
                    <svg
                      width="47"
                      height="15"
                      viewBox="0 0 47 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="min-h-4 min-w-12"
                    >
                      <path
                        d="M46.6733 10.2526C46.5934 10.2526 46.5206 10.304 46.483 10.4116C46.0013 11.7797 45.3503 12.5935 44.8145 12.5935C44.5066 12.5935 44.3821 12.2521 44.3821 11.7166C44.3821 10.3765 45.1881 7.53508 45.5947 6.23948C45.6417 6.08279 45.6722 5.94247 45.6722 5.8232C45.6722 5.44668 45.4654 5.26193 44.9531 5.26193C44.4009 5.26193 43.8064 5.47708 43.2282 6.48503C43.0285 5.59635 42.4245 5.20814 41.5809 5.20814C40.6056 5.20814 39.6633 5.83255 38.8878 6.84518C38.1123 7.85781 37.2005 8.1899 36.5143 8.02619C37.0078 6.82413 37.1911 5.9261 37.1911 5.25959C37.1911 4.21422 36.6717 3.58279 35.8328 3.58279C34.5567 3.58279 33.8212 4.7942 33.8212 6.06876C33.8212 7.05332 34.2701 8.06595 35.257 8.55706C34.4298 10.4186 33.222 12.1024 32.7637 12.1024C32.1715 12.1024 31.9976 9.2189 32.0305 7.15622C32.0516 5.97287 32.1503 5.91207 32.1503 5.55426C32.1503 5.34846 32.0164 5.20814 31.4783 5.20814C30.2234 5.20814 29.8356 6.2652 29.7768 7.47895C29.7579 7.94021 29.6853 8.39772 29.5606 8.84238C29.0366 10.7039 27.9556 12.1165 27.2506 12.1165C26.9239 12.1165 26.8346 11.7914 26.8346 11.3658C26.8346 10.0257 27.589 8.35126 27.589 6.92236C27.589 5.87231 27.126 5.20814 26.2542 5.20814C25.2272 5.20814 23.8689 6.42423 22.5835 8.70206C23.0065 6.95744 23.1804 5.26894 21.9279 5.26894C21.6561 5.27298 21.3893 5.34196 21.15 5.47007C21.0696 5.50746 21.0025 5.56805 20.9572 5.64397C20.9119 5.71988 20.8907 5.80759 20.8962 5.8957C21.0161 7.75257 19.3922 12.5094 17.8529 12.5094C17.5733 12.5094 17.437 12.2077 17.437 11.7212C17.437 10.3789 18.2407 7.54443 18.6449 6.24649C18.6966 6.07811 18.7248 5.93312 18.7248 5.80683C18.7248 5.45136 18.5039 5.26894 18.0033 5.26894C17.4534 5.26894 16.8566 5.47708 16.2808 6.48503C16.0787 5.59635 15.4747 5.20814 14.6311 5.20814C13.2469 5.20814 11.7006 6.66511 11.0215 8.56408C10.1121 11.0992 8.27905 13.5477 5.81155 13.5477C3.572 13.5477 2.38995 11.6932 2.38995 8.76286C2.38995 4.53227 5.51075 1.07577 7.8255 1.07577C8.93235 1.07577 9.4611 1.77736 9.4611 2.85313C9.4611 4.15575 8.73025 4.76146 8.73025 5.25725C8.73025 5.40926 8.85715 5.55893 9.1086 5.55893C10.1144 5.55893 11.2988 4.3826 11.2988 2.7783C11.2988 1.17399 9.98985 0 7.6751 0C3.8493 0 0 3.83068 0 8.73948C0 12.645 1.9364 15 5.28515 15C7.56935 15 9.5645 13.232 10.6408 11.1693C10.763 12.8789 11.5432 13.7722 12.7323 13.7722C13.7898 13.7722 14.6452 13.1455 15.2985 12.044C15.5499 13.1969 16.2174 13.7605 17.0845 13.7605C18.0785 13.7605 18.9104 13.1338 19.7024 11.9691C19.6906 12.8835 19.8998 13.7442 20.6964 13.7442C21.0724 13.7442 21.5213 13.6576 21.6012 13.3302C22.4401 9.87839 24.5128 7.06034 25.1474 7.06034C25.3354 7.06034 25.3871 7.24041 25.3871 7.45323C25.3871 8.39102 24.722 10.3134 24.722 11.5412C24.722 12.8672 25.2883 13.7442 26.4586 13.7442C27.7558 13.7442 29.0742 12.1632 29.9531 9.85267C30.2281 12.0112 30.8226 13.7535 31.7532 13.7535C32.8953 13.7535 34.9233 11.3611 36.1524 8.82834C36.6341 8.88915 37.358 8.87278 38.0536 8.384C37.7575 9.13003 37.5835 9.94621 37.5835 10.7624C37.5835 13.1127 38.7115 13.7722 39.6821 13.7722C40.7372 13.7722 41.5927 13.1455 42.2483 12.044C42.4645 13.0379 43.0167 13.7582 44.0319 13.7582C45.6205 13.7582 47 12.1422 47 10.8162C47 10.4654 46.8496 10.2526 46.6733 10.2526ZM13.7005 12.4696C13.0589 12.4696 12.8075 11.8265 12.8075 10.8676C12.8075 9.20253 13.952 6.42189 15.1622 6.42189C15.6909 6.42189 15.8907 7.04163 15.8907 7.79935C15.8907 9.49018 14.8026 12.4696 13.7005 12.4696ZM35.6753 7.5C35.2923 7.04631 35.1536 6.42891 35.1536 5.87933C35.1536 5.20112 35.4027 4.62816 35.7012 4.62816C35.9996 4.62816 36.0913 4.92049 36.0913 5.32741C36.0913 6.00795 35.8469 7.00187 35.6753 7.5ZM40.6503 12.4696C40.0088 12.4696 39.7573 11.7306 39.7573 10.8676C39.7573 9.26099 40.9017 6.42189 42.1214 6.42189C42.6501 6.42189 42.8382 7.03695 42.8382 7.79935C42.8382 9.49018 41.7689 12.4696 40.6503 12.4696Z"
                        fill="#0A0A0A"
                      />
                    </svg>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}
